/**
 * Flow.js type definitions and some similar enumerables.
 */

export type AnswerType = {
  action: number,
  id: number,
  text: string,
};

/**
 * Module that comes from the backend.
 */
export type Module = {
  id: number, // Number in the modules dictionary.
  pk: number, // ID in the database. For debugging only.
  text: string,
  type: string,
  key: string,
  action: number,
  role: string,
  link_href: string,
  answers: Array<AnswerType>,
};

/**
 * Module that is actually stored in the store as an `activeModule`
 * and which is operated by everywhere in the app.
 * Same as regular `Module` but enriched via `ModuleExtensionMixin`.
 */
export type RichModule = Module & {
  activeFloor: number,
  isGallery: () => boolean,
  isInChatGallery: () => boolean,
  isInChatFloorplan: () => boolean,
  isInChatDigitalTour: () => boolean,
  isFloorplan: () => boolean,
  isAdvancedFloorplan: () => boolean,
  isVideo: () => boolean,
  isDigitalTour: () => boolean,
  shouldShowGallery: () => boolean,
};

export const SlackAnimationStatus = {
  hide: "hide",
  show: "show",
  off: "off",
};

/**
 * This does not really make sense for me anymore - why do we have this separated from
 * ModuleTypes?
 */
export const ModuleRoles = {
  gallery: "gallery",
  pricing: "pricing",
  floorplan: "floorplan",
  advancedFloorplan: "advanced_floorplan",
  video: "videos",
  digitalTour: "digital_tours",
  // Made this things be Roles because this way their inchat answers need to be minified
  // once user has proceeded to another module.
  inChatGallery: "inchat_gallery",
  inChatFloorplan: "inchat_floorplan",
  inChatDigitalTour: "inchat_digital_tours",
};

export const GalleryItemType = {
  YOUTUBE: "youtube",
  IFRAME: "iframe",
  IMAGE: "image",
};

export const ModuleTypes = {
  datePicker: "date_picker",
  submit: "submit",
  sms: "sms",
  link: "link",
  click: "click",
  eval: "eval",
  multiOption: "multi_option",
  slack: "slack",
  outOfBudget: "out_of_budget",
  email: "email",
  phone: "phone",
  text: "text",
  option: "option",
  instantAnswers: "instant_answers",
  locations: "locations", // Query and show nearest locations.
  financialResults: "financial_results", // Just some static text, buttons and svg image.
  submitJobApplicant: "submit_job_applicant",
  guide: "guide", // A map with local points of interest.
};

/**
 * The idea here is slightly different from regular `ModuleTypes` as follows:
 * `ModuleTypes` define the actual value of the module.type field in the config json.
 * But `BackendModuleTypes` are not reflected in the config, they just symbolize the meaning
 * (the role, lol, but that's also a different thing) of the module and can be different
 * for different chats.
 */
export const BackendModuleTypes = {
  OPEN_PRICING: "OPEN_PRICING",
  OPEN_SCHEDULE_TOUR: "OPEN_SCHEDULE_TOUR", // used in welcome back message
  ANSWER_FLOW: "ANSWER_FLOW", // used in welcome back message
  OPEN_FLOORPLAN: "OPEN_FLOORPLAN",
  OPEN_GALLERY: "OPEN_GALLERY",
};

export const ChatModes = {
  circle: "circle",
  long: "long",
  teaser: "teaser",
  open: "open",
};

export const MessageTypes = {
  BOT_MESSAGE: "BOT_MESSAGE", // Used for the modules.
  USER_MESSAGE: "USER_MESSAGE",
  INTERACTIVE_USER_MESSAGE: "INTERACTIVE_USER_MESSAGE",
  EXTRA_BOT_MESSAGE: "EXTRA_BOT_MESSAGE", // Used e.g. for comments about incorrect user input.
  NEW_SESSION: "NEW_SESSION", // Indicate a new conversation session start.
  IA_USER_MESSAGE: "IA_USER_MESSAGE", // Instant answers user message.
  IA_BOT_MESSAGE: "IA_BOT_MESSAGE", // Instant answers admin message.
  IA_FEEDBACK_MESSAGE: "IA_FEEDBACK_MESSAGE",
  LOCATIONS_MESSAGE: "LOCATIONS_MESSAGE",
  ERROR_MESSAGE: "ERROR_MESSAGE", // Same as EXTRA_BOT_MESSAGE but with the `answers` field.
};

export type Button = {
  id: number,
  customCSS: string,
  cssSelector: string,
  htmlContent: string,
  moduleToOpen: number,
  urlLimit: ?number,
  replace: string,
};

type UserData = {
  care: string,
  what: string,
  who: string,
  when: string,
  first_name: string,
  phone: string,
  email: string,
  zip: string,
  speak_with_expert: ?string, // Specially for Ashley River Plantation Assisted Living
  monthly_income: string, // Ashley. e.g.:
  // "$1,000 - $2,000"
  // "$2,001 - $3,000"
  // "$5,000+"
};

export type ChatConfig = {|
  providerID: number, // Used in the IA API.
  coordinates: ?Array<number>,
  apiButtonURL: string,
  apiChatStartURL: string,
  apiChatUpdateURL: string,
  apiChatEventURL: string,
  apiLocationsURL: string,
  backgroundColor: string, // seems to be not used
  visitorUpdateURL: string,
  backgroundColor: string,
  brandColor: string,
  buttonColor: string,
  buttons: Array<Button>,
  color: string,
  createURL: string,
  customJavascript: string,
  disableSmallerDevices: boolean,
  fontFamily: string,
  fontSize: string,
  fullNameRequired: boolean,
  iconLogo: string,
  introText: string,
  pause: number,
  position: string,
  sameDayTour: boolean,
  showMaxTimes: ?number,
  teaser: string,
  teaserDotColor: string,
  teaserScrollDelay: number,
  name: string,
  pk: number,
  images: Array<string>,
  videos: Array<string>,
  digital_tours: Array<string>,
  timesAvailable: { [number]: Array<Array<number>> },
  timezoneName: string,
  timezoneOffset: string,
  prices: Array<any>,
  filterPricing: boolean,
  showContactInformationMessage: ?boolean,
  apiResendLeadEmailURL: string,
  modulesByName: { [string]: number },
|};

/**
 * Part of the state, which holds fields related to IA.
 */
export type IAstate = {
  suggestions: Array<any>,
  wrongAnswersInRow: number,
  hasGreeted: boolean, // Do not greet user twice when entering IA mode.
  lastQuestion: string,
  queue: Array<any>,
  showQuickActions: boolean,
};

export type GuideState = {
  selectedCategory: string,
};

export type GlobalState = {|
  textInputValue: string,
  errorMessage: { [number]: string },
  editingMessageNum: ?number,
  workWithHistory: boolean,
  activeModule: ?Module,
  config: ?ChatConfig,
  conversation: any,
  familyAssetsAdmin: boolean,
  isTeaserVisible: boolean,
  isWeekend: boolean,
  leadID: ?string,
  localHistoryExists: boolean,
  localHistoryLoaded: boolean,
  messagesHistory: any,
  modules: ?Array<Module>,
  shouldRestoreSession: boolean,
  showConfirmSelectionButton: boolean,
  showDatePicker: boolean,
  showTextInput: boolean,
  teaserClosed: ?string,
  userData: UserData,
  uuid: ?string, // ChatUser unique id.
  visitorUUID: ?string, // Visitor unique id.
  weekendInactive: boolean,
  botMessageSound: any,
  titleFlashingInterval: ?number,
  originalPageTitle: string,
  showAllAnswerOptions: boolean,
  maxAnswersToInitiallyShow: number,
  chatMode: string,
  slackController: ?SlackController,
  financialChatActivated: ?boolean,
  moduleBeforeGallery: ?number,
  slackAnimation: any,
  jobApplicationSubmitted: boolean,
  ia: IAstate,
  guide: GuideState,
|};

export type UrlConfig = {
  staticConfig: any, // uses chat config from json file in the repo, instead of loading it from some api server.
  localConfig: any, // loads chat config from locally run django application, instead of familyassets.com.
  noAnimation: any, // disabled all animations and delayed render of elements.
  chatOpen: any, // forces the chat to be open upon the page load.
  clearStorage: any, // clear localstorage before config loading.
  simulateDomain: any, // str domain name - simulates as if the chat was run on a different domain.
  openFamilyAssetsBot: any, // go to block by it's number right after page load.
  seleniumConfig: any, // load seleniumChatConfigExample
  personalizedMessages: any, // load personalizedMessages config
  noFlashing: any, // don't flash the page title.
  forceOpen: any, // open chat at max instead of teaser.
  calendarTesting: any, // fix date in the calendar widget.
  hackPageHeight: any, // for browserstack visual snapshots testing - to prevent our testing page from being 4000px tall.
  noCaret: any, // hide input field caret (actually make the text transparent) for selenium tests.
  shouldShowContactInformation: any, // Show contact information warning at the bottom of the chat.
  noSubmit: any,
  disableMobile: any, // Show custom teaser popup preview for mobile devices.
  financialChatActivated: any,
  providerProfileId: any, // To simulate loading pages like
  // https://www.familyassets.com/assisted-living/south-carolina/charleston/ashley-river-plantation
  // which set window.familyAssetsChatProviderProfile
  guide: any, // guide testing
  localIframe: any, // also guide testing
};

export type MessageContextType = {
  isWelcomeBackMessage: boolean,
  isFirstModule: boolean,
  isPricing: boolean,
  isLocations: boolean,
  isErrorMessage: boolean,
  isLastMessage: boolean,
  isFeedback: boolean,
  messageNum: number,
  messageType: $Keys<typeof MessageTypes>,
  fromHistory: boolean,
  messageData: any,
  module: Module,
  customMobilePreview: boolean,
};
